import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDate, fmtTons } from "@/lib/format";
import CustomerAutocomplete from "@/components/CustomerAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Trash2, Search, Receipt, Download } from "lucide-react";

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [plants, setPlants] = useState([]);
  const [search, setSearch] = useState("");
  const [plantFilter, setPlantFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (plantFilter !== "all") params.plant = plantFilter;
    if (monthFilter) params.month = monthFilter;
    const { data } = await api.get("/sales", { params });
    setSales(data);
  }, [search, plantFilter, monthFilter]);

  useEffect(() => { api.get("/plants").then((r) => setPlants(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/sales/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sfx_sales_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Sales exported to CSV");
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/sales/${editing.id}`, {
        sale_date: editing.sale_date, customer_name: editing.customer_name,
        tons: Number(editing.tons), vehicle_no: editing.vehicle_no, plant: editing.plant,
      });
      toast.success("Sale updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/sales/${deleting.id}`);
      toast.success("Sale deleted");
      setDeleting(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-chivo font-black text-3xl text-slate-900">Sales</h1>
          <p className="text-slate-500 mt-1">All ice-sale records. Edit or delete as needed.</p>
        </div>
        <Button variant="outline" data-testid="export-sales-button" onClick={exportCsv} disabled={exporting}>
          <Download className="h-4 w-4 mr-1" /> {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="sales-search" className="pl-9" placeholder="Search customer / vehicle"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Input type="month" data-testid="sales-month-filter" className="w-44"
          value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} />
        <Select value={plantFilter} onValueChange={setPlantFilter}>
          <SelectTrigger className="w-40" data-testid="sales-plant-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plants</SelectItem>
            {plants.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="sales-table">
          <thead className="bg-slate-50 text-slate-500 text-left border-b border-slate-200">
            <tr>
              <th className="p-3 font-semibold">Date</th><th className="p-3 font-semibold">Customer</th>
              <th className="p-3 text-right font-semibold">Tons</th><th className="p-3 font-semibold">Vehicle</th>
              <th className="p-3 font-semibold">Plant</th><th className="p-3 font-semibold">Entered by</th>
              <th className="p-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sales.map((s) => (
              <tr key={s.id} className="border-t border-slate-100 hover:bg-sky-50/40 transition-colors" data-testid={`sale-row-${s.id}`}>
                <td className="p-3 whitespace-nowrap">{fmtDate(s.sale_date)}</td>
                <td className="p-3 font-medium text-slate-900">{s.customer_name}{!s.customer_id && <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">new</span>}</td>
                <td className="p-3 text-right tabular font-semibold">{fmtTons(s.tons)}</td>
                <td className="p-3">{s.vehicle_no}</td>
                <td className="p-3"><span className={`text-xs font-bold px-2 py-1 rounded ${s.plant === "SFX 2" ? "bg-indigo-100 text-indigo-700" : "bg-sky-100 text-sky-700"}`}>{s.plant}</span></td>
                <td className="p-3 text-slate-400 text-xs">{s.created_by_name}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" data-testid={`edit-sale-${s.id}`} onClick={() => setEditing({ ...s })}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`delete-sale-${s.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleting(s)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
            {sales.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-slate-400"><Receipt className="h-8 w-8 mx-auto mb-2 opacity-40" />No sales found.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-sale-dialog">
          <DialogHeader><DialogTitle>Edit Sale</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Date</Label><Input type="date" data-testid="edit-date" value={editing.sale_date} onChange={(e) => setEditing({ ...editing, sale_date: e.target.value })} /></div>
              <div><Label>Customer</Label><CustomerAutocomplete value={editing.customer_name} onChange={(v) => setEditing({ ...editing, customer_name: v })} testid="edit-customer" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tons</Label><Input type="number" step="0.01" data-testid="edit-tons" value={editing.tons} onChange={(e) => setEditing({ ...editing, tons: e.target.value })} /></div>
                <div><Label>Vehicle</Label><Input data-testid="edit-vehicle" value={editing.vehicle_no} onChange={(e) => setEditing({ ...editing, vehicle_no: e.target.value })} /></div>
              </div>
              <div><Label>Plant</Label>
                <Select value={editing.plant} onValueChange={(v) => setEditing({ ...editing, plant: v })}>
                  <SelectTrigger data-testid="edit-plant"><SelectValue /></SelectTrigger>
                  <SelectContent>{plants.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="save-edit-button" className="bg-sky-600 hover:bg-sky-700" onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this sale?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && `${fmtDate(deleting.sale_date)} · ${deleting.customer_name} · ${fmtTons(deleting.tons)} tons.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" className="bg-red-600 hover:bg-red-700" onClick={doDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
